import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, FormFeedback, Input, Label, Nav, NavItem, NavLink, Spinner, TabContent, TabPane } from "reactstrap";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import ParticlesAuth from "./ParticlesAuth";
import { createOwnShop, joinShopByInvite } from "../../helpers/backend_helper";
import axios from "../../helpers/axiosConfig";
import { setToken } from "../../slices/authSlice";
import { resolveApiMessage } from "../../helpers/apiMessage";

const getErrorMessage = (error: any): string => {
  return resolveApiMessage(error, { fallbackMessage: "Tạo cửa hàng thất bại. Vui lòng thử lại." });
};

const normalizeInviteCode = (value: string) => value.trim();

const CreateShop = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = useMemo(() => (searchParams.get("tab") === "join" ? "join" : "create"), [searchParams]);
  const [activeTab, setActiveTab] = useState<"create" | "join">(initialTab);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isJoinSubmitting, setIsJoinSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [joinErrorMessage, setJoinErrorMessage] = useState("");

  useEffect(() => {
    document.title = "Hệ thống quản lý đặt món - Tạo cửa hàng";
  }, []);

  const formik = useFormik({
    initialValues: {
      name: "",
      address: "",
      phone: "",
      referral_code: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Vui lòng nhập tên cửa hàng"),
      address: Yup.string().required("Vui lòng nhập địa chỉ cửa hàng"),
      phone: Yup.string().required("Vui lòng nhập số điện thoại cửa hàng"),
      referral_code: Yup.string().max(255, "Mã giới thiệu không được vượt quá 255 ký tự"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setErrorMessage("");

      try {
        await createOwnShop(values);

        const response = await axios.get("/api/user", { withCredentials: true });
        const responseData = response?.data ?? response;
        const userData = responseData?.user ?? responseData;

        if (userData) {
          const session = { user: userData };
          localStorage.setItem("authUser", JSON.stringify(session));
          dispatch(setToken(session));
        }

        navigate("/order");
      } catch (error) {
        setErrorMessage(getErrorMessage(error));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const joinFormik = useFormik({
    initialValues: {
      invite_code: searchParams.get("invite") ?? "",
    },
    validationSchema: Yup.object({
      invite_code: Yup.string()
        .transform((value) => normalizeInviteCode(value ?? ""))
        .required("Vui lòng nhập mã mời"),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsJoinSubmitting(true);
      setJoinErrorMessage("");

      try {
        await joinShopByInvite({ invite_code: normalizeInviteCode(values.invite_code) });

        const response = await axios.get("/api/user", { withCredentials: true });
        const responseData = response?.data ?? response;
        const userData = responseData?.user ?? responseData;

        if (userData) {
          const session = { user: userData };
          localStorage.setItem("authUser", JSON.stringify(session));
          dispatch(setToken(session));
        }

        navigate("/order");
      } catch (error) {
        setJoinErrorMessage(getErrorMessage(error));
      } finally {
        setIsJoinSubmitting(false);
      }
    },
  });

  return (
    <ParticlesAuth>
      <div className="container">
        <div className="row justify-content-center">
          <div className="p-3">
            <div className="text-center text-white mb-4">
              <h2 className="text-white">Tạo cửa hàng đầu tiên</h2>
              <p className="text-white-50 mb-0">
                Chào mừng bạn! Hãy tạo cửa hàng để bắt đầu sử dụng hệ thống.
              </p>
            </div>

            <div className="bg-white rounded p-4">
              <Nav tabs className="mb-3">
                <NavItem>
                  <NavLink
                    className={activeTab === "create" ? "active" : ""}
                    style={{ cursor: "pointer" }}
                    onClick={() => setActiveTab("create")}
                  >
                    Tạo shop
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === "join" ? "active" : ""}
                    style={{ cursor: "pointer" }}
                    onClick={() => setActiveTab("join")}
                  >
                    Tham gia shop
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="create">
                  {errorMessage ? <Alert color="danger">{errorMessage}</Alert> : null}

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      formik.handleSubmit();
                    }}
                  >
                    <div className="form-group text-start mb-3">
                      <span>Tên cửa hàng</span>
                      <Label>
                        <i className="mdi mdi-storefront-outline"></i>
                      </Label>
                      <Input
                        name="name"
                        type="text"
                        placeholder="Ví dụ: Quán Cà Phê Mộc"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(formik.touched.name && formik.errors.name)}
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <FormFeedback>{formik.errors.name}</FormFeedback>
                      ) : null}
                    </div>

                    <div className="form-group text-start mb-3">
                      <span>Địa chỉ cửa hàng</span>
                      <Label>
                        <i className="mdi mdi-map-marker-outline"></i>
                      </Label>
                      <Input
                        name="address"
                        type="text"
                        placeholder="Ví dụ: 123 Nguyễn Huệ, Quận 1"
                        value={formik.values.address}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(formik.touched.address && formik.errors.address)}
                      />
                      {formik.touched.address && formik.errors.address ? (
                        <FormFeedback>{formik.errors.address}</FormFeedback>
                      ) : null}
                    </div>

                    <div className="form-group text-start mb-4">
                      <span>Số điện thoại cửa hàng</span>
                      <Label>
                        <i className="mdi mdi-phone-outline"></i>
                      </Label>
                      <Input
                        name="phone"
                        type="text"
                        placeholder="Ví dụ: 0901234567"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(formik.touched.phone && formik.errors.phone)}
                      />
                      {formik.touched.phone && formik.errors.phone ? (
                        <FormFeedback>{formik.errors.phone}</FormFeedback>
                      ) : null}
                    </div>

                    <div className="form-group text-start mb-4">
                      <span>Mã giới thiệu (tuỳ chọn)</span>
                      <Label>
                        <i className="mdi mdi-ticket-percent-outline"></i>
                      </Label>
                      <Input
                        name="referral_code"
                        type="text"
                        placeholder="Nhập mã giới thiệu"
                        value={formik.values.referral_code}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        invalid={Boolean(formik.touched.referral_code && formik.errors.referral_code)}
                      />
                      {formik.touched.referral_code && formik.errors.referral_code ? (
                        <FormFeedback>{formik.errors.referral_code}</FormFeedback>
                      ) : null}
                    </div>

                    <Button className="btn btn-warning btn-lg w-100" type="submit" disabled={isSubmitting}>
                      {isSubmitting ? <Spinner size="sm" className="me-2" /> : null}
                      Tạo cửa hàng
                    </Button>
                  </form>
                </TabPane>

                <TabPane tabId="join">
                  {joinErrorMessage ? <Alert color="danger">{joinErrorMessage}</Alert> : null}
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      joinFormik.handleSubmit();
                    }}
                  >
                    <div className="form-group text-start mb-4">
                      <span>Mã mời</span>
                      <Label>
                        <i className="mdi mdi-link-variant"></i>
                      </Label>
                      <Input
                        name="invite_code"
                        type="text"
                        placeholder="Nhập mã mời"
                        value={joinFormik.values.invite_code}
                        onChange={joinFormik.handleChange}
                        onBlur={joinFormik.handleBlur}
                        invalid={Boolean(joinFormik.touched.invite_code && joinFormik.errors.invite_code)}
                      />
                      {joinFormik.touched.invite_code && joinFormik.errors.invite_code ? (
                        <FormFeedback>{joinFormik.errors.invite_code}</FormFeedback>
                      ) : null}
                    </div>

                    <Button className="btn btn-warning btn-lg w-100" type="submit" disabled={isJoinSubmitting}>
                      {isJoinSubmitting ? <Spinner size="sm" className="me-2" /> : null}
                      Tham gia cửa hàng
                    </Button>
                  </form>
                </TabPane>
              </TabContent>
              <Link className="d-block mt-3 mb-1" to="/logout">Đăng xuất</Link>
            </div>
          </div>
        </div>
      </div>
    </ParticlesAuth>
  );
};

export default CreateShop;
